import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC, useMemo, useState } from 'react';
import { feedback } from 'react-feedbacker';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'urql';

import { useFeedbackMessages } from '@/bits';
import {
  Card,
  CardCloseButton,
  CardOptions,
  ErrorMessage,
  NakedForm,
  NumberField,
  SelectField,
  SelectOption,
  SubmitButton,
  TextareaField,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import { playerSelfExclusionDetailsFragment } from '@/fragments/PlayerSelfExclusionDetails';
import { useIsMounted } from '@/hooks';
import { Nullable } from '@/types';
import { mapVariables } from '@/utils';
import {
  PlayerSelfExclusionFormQuery,
  PlayerSelfExclusionFormQueryVariables,
  PlayerSelfExclusionMutation,
  PlayerSelfExclusionMutationVariables,
} from './__generated__/PlayerSelfExclusionForm';

const query = graphql`
  query SanityPlayerSelfExclusionForm {
    sanityPlayerSelfExclusionForm {
      title {
        ...SanityLocaleString
      }
      durationLabel {
        ...SanityLocaleString
      }
      selectDuration {
        ...SanityLocaleString
      }
      duration24Hours {
        ...SanityLocaleString
      }
      duration48Hours {
        ...SanityLocaleString
      }
      duration7Days {
        ...SanityLocaleString
      }
      duration1Month {
        ...SanityLocaleString
      }
      duration2Months {
        ...SanityLocaleString
      }
      duration3Months {
        ...SanityLocaleString
      }
      duration6Months {
        ...SanityLocaleString
      }
      duration12Months {
        ...SanityLocaleString
      }
      durationIndefinite {
        ...SanityLocaleString
      }
      durationCustom {
        ...SanityLocaleString
      }
      dateLabel {
        ...SanityLocaleString
      }
      commentLabel {
        ...SanityLocaleString
      }
      submit {
        ...SanityLocaleString
      }
    }
  }
`;

const playerQuery = gql`
  query PlayerSelfExclusionForm($playerId: ID!) {
    player(playerId: $playerId) {
      id
    }
  }
`;

const playerMutation = gql`
  mutation PlayerSelfExclusion(
    $playerId: ID!
    $exclusionDaysArg: Int
    $comment: String!
  ) {
    selfExcludeV4(
      playerId: $playerId
      comment: $comment
      exclusionDaysArg: $exclusionDaysArg
    ) {
      id
      ...PlayerSelfExclusionDetails
    }
  }
  ${playerSelfExclusionDetailsFragment}
`;

enum SpecialDurations {
  Indefinite = 'Indefinite',
  Custom = 'Custom',
}

const isAnjouan = process.env.GATSBY_LICENSE === 'ANJ';
const MIN_ANJOUAN_DAYS = 180;

const useDurationOptions = (
  form: Queries.SanityPlayerSelfExclusionFormQuery['sanityPlayerSelfExclusionForm'],
) => {
  const { t } = useTranslate();

  return useMemo(() => {
    if (!form) {
      return [];
    }

    if (isAnjouan) {
      const anjOptions: SelectOption[] = [
        {
          label: t(form.selectDuration),
          value: '',
        },
        {
          label: t(form.duration48Hours),
          value: '2',
        },
        {
          label: t(form.duration7Days),
          value: '7',
        },
        {
          label: t(form.duration1Month),
          value: '30',
        },
        {
          label: t(form.duration2Months),
          value: '60',
        },
        {
          label: t(form.duration3Months),
          value: '90',
        },
        {
          label: t(form.duration6Months),
          value: '180',
        },
        {
          label: t(form.duration12Months),
          value: '365',
        },
        {
          label: t(form.durationIndefinite),
          value: SpecialDurations.Indefinite,
        },
        {
          label: t(form.durationCustom),
          value: SpecialDurations.Custom,
        },
      ];

      return anjOptions;
    }

    const options: SelectOption[] = [
      {
        label: t(form.selectDuration),
        value: '',
      },
      {
        label: t(form.duration24Hours),
        value: '1',
      },
      {
        label: t(form.duration7Days),
        value: '7',
      },
      {
        label: t(form.duration1Month),
        value: '30',
      },
      {
        label: t(form.duration3Months),
        value: '90',
      },
      {
        label: t(form.duration6Months),
        value: '180',
      },
      {
        label: t(form.duration12Months),
        value: '365',
      },
      {
        label: t(form.durationIndefinite),
        value: SpecialDurations.Indefinite,
      },
      {
        label: t(form.durationCustom),
        value: SpecialDurations.Custom,
      },
    ];

    return options;
  }, [form, t]);
};

type FormValues = {
  comment: string;
  duration: string;
  customDays: string;
};

const getExclusionDays = (values: FormValues): number | null => {
  if (values.duration === SpecialDurations.Custom) {
    return parseInt(values.customDays, 10);
  }
  if (values.duration === SpecialDurations.Indefinite) {
    return null;
  }
  return parseInt(values.duration, 10);
};

const PlayerSelfExclusionForm: FC<{ playerId: string }> = ({ playerId }) => {
  const staticData =
    useStaticQuery<Queries.SanityPlayerSelfExclusionFormQuery>(query);
  const feedbackMessages = useFeedbackMessages();

  const defaultValues: FormValues = {
    comment: '',
    duration: '',
    customDays: '',
  };

  const formMethods = useForm({
    defaultValues,
  });

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();

  const form = staticData.sanityPlayerSelfExclusionForm;

  const [{ data }] = useQuery<
    PlayerSelfExclusionFormQuery,
    PlayerSelfExclusionFormQueryVariables
  >({
    query: playerQuery,
    variables: { playerId },
  });

  const [selfExcludeState, selfExclude] = useMutation<
    PlayerSelfExclusionMutation,
    PlayerSelfExclusionMutationVariables
  >(playerMutation);

  const { close } = useModalContext();
  const { t } = useTranslate();

  const durationOptions = useDurationOptions(form);

  const onSubmit = (values: FormValues) => {
    if (
      !data?.player?.id ||
      !values.comment.trim() ||
      !values.duration ||
      (values.duration === SpecialDurations.Custom && !values.customDays)
    ) {
      return;
    }

    selfExclude(
      mapVariables({
        playerId: data?.player.id,
        comment: values.comment,
        exclusionDaysArg: getExclusionDays(values),
      }),
    ).then((res) => {
      if (res.error?.message && isMounted) {
        setErrorMessage(res.error.message);
      } else if (close) {
        close();
        feedback.success(t(feedbackMessages.success));
      }
    });
  };

  if (!form || !data) {
    return null;
  }

  const { duration } = formMethods.watch();

  return (
    <Card
      size="lg"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <div className="p-3">
        <NakedForm
          methods={formMethods}
          onSubmit={onSubmit}
          className="grid grid-cols-2 sm:grid-cols-3 gap-6"
        >
          <SelectField
            title={t(form.durationLabel)}
            name="duration"
            id="PlayerSelfExclusion__duration"
            options={durationOptions}
            required
          />
          {duration === SpecialDurations.Custom && (
            <NumberField
              name="customDays"
              id="PlayerSelfExclusion__customDays"
              title="Days"
              min={isAnjouan ? MIN_ANJOUAN_DAYS : 1}
            />
          )}
          <TextareaField
            title={t(form.commentLabel)}
            name="comment"
            id="PlayerSelfExclusion__comment"
            className="col-span-full"
            required
          />
          <ErrorMessage message={errorMessage} />
          <SubmitButton
            value={t(form.submit)}
            disabled={selfExcludeState.fetching}
          />
        </NakedForm>
      </div>
    </Card>
  );
};

export default PlayerSelfExclusionForm;
